import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BookOpen, ArrowLeft, PlayCircle, Edit, Users, FileQuestion } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseDetail({ user }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(null); // null = loading, true/false = checked
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourseDetails();
    checkEnrollment();
  }, [courseId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const loadCourseDetails = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
      } else if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      }
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  const checkEnrollment = async () => {
    try {
      const response = await fetch(`${API}/enrollments/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const enrollments = await response.json();
        const enrolled = enrollments.some(e => e.course_id === courseId);
        setIsEnrolled(enrolled);
      } else {
        setIsEnrolled(false);
      }
    } catch (error) {
      console.error('Failed to check enrollment');
      setIsEnrolled(false);
    }
  };

  const handleEnroll = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/courses/${courseId}/enroll`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Successfully enrolled!');
        setIsEnrolled(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Enrollment failed');
      }
    } catch (error) {
      toast.error('Enrollment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCourse = () => {
    navigate(`/courses/${courseId}/view`);
  };

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/courses')}
            data-testid="back-to-courses-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Courses</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
        {course.thumbnail && (
          <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden shadow-lg">
            <img
              src={course.thumbnail}
              alt={course.title}
              className="w-full h-48 sm:h-72 md:h-96 object-cover"
            />
          </div>
        )}

        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#344E41] mb-2 sm:mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                {course.title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-[#588157] leading-relaxed">{course.description}</p>
            </div>
            {user?.role === 'teacher' && (
              <div className="flex-shrink-0 flex flex-wrap gap-2 w-full sm:w-auto">
                <Button
                  onClick={() => navigate(`/courses/${courseId}/students`)}
                  data-testid="view-students-button"
                  variant="outline"
                  className="flex-1 sm:flex-none border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white shadow-md duration-300 rounded-lg text-sm"
                >
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" strokeWidth={1.5} />
                  Students
                </Button>
                <Button
                  onClick={() => navigate(`/courses/${courseId}/quizzes`)}
                  data-testid="view-quizzes-button"
                  variant="outline"
                  className="flex-1 sm:flex-none border-2 border-[#588157] text-[#588157] hover:bg-[#588157] hover:text-white shadow-md duration-300 rounded-lg text-sm"
                >
                  <FileQuestion className="w-4 h-4 sm:w-5 sm:h-5 mr-2" strokeWidth={1.5} />
                  Quizzes
                </Button>
                <Button
                  onClick={() => navigate(`/courses/${courseId}/edit`)}
                  data-testid="edit-course-button"
                  className="flex-1 sm:flex-none bg-[#D4A373] text-white hover:bg-[#C89563] shadow-md duration-300 rounded-lg text-sm"
                >
                  <Edit className="w-4 h-4 sm:w-5 sm:h-5 mr-2" strokeWidth={1.5} />
                  Edit
                </Button>
              </div>
            )}
          </div>
        </div>

        <Card className="p-4 sm:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl mb-6 sm:mb-8">
          <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm text-[#A3B18A] mb-1">Duration</p>
              <p className="text-lg sm:text-2xl font-bold text-[#344E41]">{course.duration_weeks} weeks</p>
            </div>
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm text-[#A3B18A] mb-1">Lessons</p>
              <p className="text-lg sm:text-2xl font-bold text-[#344E41]">{course.content?.length || 0}</p>
            </div>
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm text-[#A3B18A] mb-1">Level</p>
              <p className="text-lg sm:text-2xl font-bold text-[#344E41]">All Levels</p>
            </div>
          </div>

          {isEnrolled === null ? (
            <Button
              disabled
              className="w-full bg-[#A3B18A] text-white shadow-md font-medium rounded-lg py-4 sm:py-6 text-base sm:text-lg"
            >
              Loading...
            </Button>
          ) : isEnrolled ? (
            <div className="space-y-3">
              <Button
                onClick={handleStartCourse}
                data-testid="start-course-button"
                className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-4 sm:py-6 text-base sm:text-lg"
              >
                <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" strokeWidth={1.5} />
                Continue Course
              </Button>
              <Button
                onClick={() => navigate(`/courses/${courseId}/quizzes`)}
                data-testid="view-quizzes-student-button"
                variant="outline"
                className="w-full border-2 border-[#588157] text-[#588157] hover:bg-[#588157] hover:text-white shadow-md duration-300 rounded-lg py-3"
              >
                <FileQuestion className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Take Quizzes
              </Button>
            </div>
          ) : user?.role === 'student' ? (
            <div className="text-center p-4 sm:p-6 bg-[#E9EDC9]/50 rounded-xl">
              <p className="text-[#588157] font-medium mb-2">
                Enrollment Required
              </p>
              <p className="text-sm text-[#A3B18A]">
                Please contact your teacher or administrator to be enrolled in this course.
              </p>
            </div>
          ) : (
            <Button
              onClick={handleEnroll}
              disabled={loading}
              data-testid="enroll-course-button"
              className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-4 sm:py-6 text-base sm:text-lg"
            >
              {loading ? 'Enrolling...' : 'Enroll Now'}
            </Button>
          )}
        </Card>

        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-[#344E41] mb-4 sm:mb-6" style={{ fontFamily: 'Merriweather, serif' }}>Course Content</h2>
          <div className="space-y-3 sm:space-y-4">
            {course.content?.map((item, index) => (
              <Card
                key={index}
                className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[#3A5A40]/10 rounded-full flex items-center justify-center">
                    <span className="text-[#3A5A40] font-bold text-sm sm:text-base">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-[#A3B18A] uppercase tracking-wide">{item.type}</p>
                    <h3 className="text-base sm:text-lg font-bold text-[#344E41] truncate">{item.title || `Lesson ${index + 1}`}</h3>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}